import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import Homepage from './components/Homepage';
import LoginModal from './components/LoginModal';
import MainAppV2 from './components/MainAppV2';
import PublicProfile from './components/PublicProfile';
import AdminDebugPanel from './components/AdminDebugPanel';
import AdminDebugTrigger from './components/AdminDebugTrigger';
import FeedbackPanel from './components/FeedbackPanel';
import FeedbackTrigger from './components/FeedbackTrigger';
import DatabaseTest from './components/DatabaseTest';
import debugLogger from './utils/debugLogger';
import { useSupabaseIntegration } from './utils/useSupabaseIntegration';
import { sessionManager } from './utils/sessionManager';

function App() {
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);
  const [debugPanelOpen, setDebugPanelOpen] = useState(false);
  const [feedbackPanelOpen, setFeedbackPanelOpen] = useState(false);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);

  // Initialize Supabase integration
  const supabaseIntegration = useSupabaseIntegration(user);
  const { 
    profile, 
    loading: supabaseLoading, 
    error: supabaseError,
    updateUserProfile,
    logUserEvent,
    getAllProfiles,
    getLogs,
    supabaseHelpers
  } = supabaseIntegration;

  // Check for existing session on app load
  useEffect(() => {
    const checkExistingSession = () => {
      try {
        const session = sessionManager.getSession();
        
        if (session) {
          setUser(session.user);
          debugLogger.user('Session restored from localStorage', {
            email: session.user.email,
            expiresAt: new Date(session.expiryTime).toLocaleString()
          });
        }
      } catch (error) {
        console.error('Error checking existing session:', error);
        sessionManager.clearSession();
      } finally {
        setIsInitializing(false);
      }
    };

    checkExistingSession();
  }, []);

  // Handle session expiration (simplified - no monitoring needed)
  const handleSessionExpired = () => {
    debugLogger.user('Session expired, user will need to log in again');
    setUser(null);
    sessionManager.clearSession();
  };

  // Initialize admin debug logging
  useEffect(() => {
    debugLogger.componentMounted('App', { userAgent: navigator.userAgent });
    debugLogger.updateAppState({
      currentUser: user?.email || null,
      debugPanelOpen,
      feedbackPanelOpen,
      hasError: !!error
    });
  }, []);

  // Update app state when dependencies change
  useEffect(() => {
    debugLogger.updateAppState({
      currentUser: user?.email || null,
      debugPanelOpen,
      feedbackPanelOpen,
      hasError: !!error,
      lastUpdate: new Date().toISOString()
    });
  }, [user, debugPanelOpen, feedbackPanelOpen, error]);

  const handleLoginSuccess = (credentialResponse) => {
    try {
      // Decode the JWT token to get user information
      const decoded = jwtDecode(credentialResponse.credential);
      console.log('Decoded user info:', decoded);
      
      // Store user data using our own session duration (ignore JWT expiry)
      const sessionStored = sessionManager.setSession(decoded);
      
      if (!sessionStored) {
        console.warn('Failed to store session, user will need to login again on refresh');
      }
      
      setUser(decoded);
      setError(null);
      setLoginModalOpen(false); // Close the modal on successful login
      
      // Log successful login
      debugLogger.user('User login successful', {
        email: decoded.email,
        name: decoded.name,
        loginTime: new Date().toISOString(),
        sessionDuration: '30 days (auto-extending)',
        sessionStored: sessionStored
      });
      debugLogger.logUserAction('User Login', {
        method: 'Google OAuth',
        email: decoded.email,
        name: decoded.name
      });
    } catch (error) {
      console.error('Error decoding token:', error);
      setError('Failed to decode user information');
      
      // Log login error
      debugLogger.error('Login token decode failed', {
        error: error.message,
        credential: credentialResponse.credential ? 'Present' : 'Missing'
      });
    }
  };

  const handleLoginError = () => {
    console.error('Google Login Failed');
    setError('Google Login Failed. Please try again.');
    
    // Log login error
    debugLogger.error('Google login failed', {
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent
    });
    debugLogger.logUserAction('Login Failed', {
      method: 'Google OAuth',
      reason: 'Google login failed'
    });
  };

  const handleLogout = () => {
    const logoutUser = user?.email || 'Unknown';
    
    // Clear stored session data
    sessionManager.clearSession();
    
    setUser(null);
    setError(null);
    
    // Log logout
    debugLogger.user('User logged out', {
      email: logoutUser,
      logoutTime: new Date().toISOString()
    });
    debugLogger.logUserAction('User Logout', {
      email: logoutUser
    });
  };

  const openLoginModal = () => {
    setLoginModalOpen(true);
    setError(null); // Clear any previous errors when opening the modal
  };

  const closeLoginModal = () => {
    setLoginModalOpen(false);
    setError(null); // Clear errors when closing the modal
  };

  // Show loading spinner while checking for existing session
  if (isInitializing) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column',
        gap: '20px'
      }}>
        <div style={{ fontSize: '24px' }}>🔄</div>
        <div>Checking authentication...</div>
      </div>
    );
  }

  return (
    <Router>
      <div className="app">
        <Routes>
          {/* Public Profile Route - accessible to everyone */}
          <Route 
            path="/profile/:userId" 
            element={
              <PublicProfile 
                currentUser={user} 
                supabaseIntegration={supabaseIntegration}
              />
            } 
          />
          
          {/* Main App Route - requires authentication */}
          <Route 
            path="/" 
            element={
              user ? (
                <MainAppV2 
                  user={user} 
                  profile={profile}
                  updateUserProfile={updateUserProfile}
                  logUserEvent={logUserEvent}
                  supabaseIntegration={supabaseIntegration}
                  onLogout={handleLogout} 
                />
              ) : (
                <>
                  <Homepage onLoginClick={openLoginModal} />
                  <LoginModal
                    isOpen={loginModalOpen}
                    onClose={closeLoginModal}
                    onLoginSuccess={handleLoginSuccess}
                    onLoginError={handleLoginError}
                    error={error}
                  />
                </>
              )
            } 
          />
          
          {/* Catch all route - redirect to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>

        {/* Admin Debug System - available on all routes */}
        <AdminDebugTrigger 
          onToggle={() => setDebugPanelOpen(!debugPanelOpen)}
          isOpen={debugPanelOpen}
          user={user}
          profile={profile}
        />
        <AdminDebugPanel 
          user={user}
          profile={profile}
          supabaseLoading={supabaseLoading}
          supabaseError={supabaseError}
          updateUserProfile={updateUserProfile}
          logUserEvent={logUserEvent}
          getAllProfiles={getAllProfiles}
          getLogs={getLogs}
          supabaseHelpers={supabaseHelpers}
          DatabaseTestComponent={DatabaseTest}
          isOpen={debugPanelOpen}
          onClose={() => setDebugPanelOpen(false)}
          onOpenFeedback={() => setFeedbackPanelOpen(true)}
        />

        {/* Feedback System - available on all routes for testers and admins */}
        <FeedbackTrigger 
          onToggle={() => setFeedbackPanelOpen(!feedbackPanelOpen)}
          isOpen={feedbackPanelOpen}
          user={user}
          profile={profile}
        />
        <FeedbackPanel 
          user={user}
          profile={profile}
          supabaseIntegration={supabaseIntegration}
          isOpen={feedbackPanelOpen}
          onClose={() => setFeedbackPanelOpen(false)}
        />
      </div>
    </Router>
  );
}

export default App; 